import { useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from '@app/components';
import { languageOptions } from '@app/constants';
import { paths } from '@app/constants/paths.constant';
import { changeLanguage, getCurrentAppTheme, showDefaultErrorToast } from '@app/utils';
import { HTTPStatusCodes } from '@data';
import { useResetPassword } from '@data/hooks';
import { isValidEmail } from '@data/utils/validation';
import {
  Button,
  Card,
  AntdConfigProvider,
  AntdForm,
  AntdGrid,
  AntdSelect,
  AntdSpace,
  AntdTypography,
  useTheme,
  isYWC,
  YWCLogoSmall,
} from '@ui';
import { Input } from '../components';

const { useBreakpoint } = AntdGrid;
const { Text, Title } = AntdTypography;

const ForgotPasswordPage = () => {
  const [form] = AntdForm.useForm();
  const { sm, md } = useBreakpoint();
  const { i18n, t } = useTranslation();
  const { bgColor, baseSelect, textColorDark } = useTheme();

  const [email, setEmail] = useState<string | null>(null);

  const { mutate: resetPassword, isPending: passwordResetting } = useResetPassword();
  const { PublicLogo, publicLogoProps, AdditionalLogo, additionalLogoProps } = getCurrentAppTheme();

  const onSubmit = (values: { email: string }) => {
    resetPassword(values, {
      onSuccess: () => {
        setEmail(values.email);
      },
      onError: (error: any) => {
        if (error?.response?.status === HTTPStatusCodes.NOT_FOUND) {
          showDefaultErrorToast(error);
        }
      },
    });
  };
  return (
    <>
      <div
        className="hidden sm:block pl-6 sm:pl-0 pt-16 pb-11 sm:pt-0 rounded-bl-3xl rounded-br-3xl"
        style={{ backgroundColor: sm ? 'transparent' : bgColor }}
      >
        {email ? (
          <Title className="text-base-100 mb-0 mt-0 sm:mt-auto">
            {t('forgot_password_page.success_title')}
          </Title>
        ) : (
          <Title className="text-base-100 mb-0 mt-0 sm:mt-auto">
            {t('forgot_password_page.title')}
          </Title>
        )}
        {isYWC && <PublicLogo {...publicLogoProps} />}
        <Text className="inline-flex p-0 text-base-100 ">
          <Trans
            i18nKey="common.back_to_login"
            components={[
              <Link to={`/${paths.sign_in}`} key="link" className="underline text-base-100 ml-1" />,
            ]}
          />
        </Text>
      </div>
      <AntdSpace />
      <Card className="border-none sm:border-solid sm:max-w-md">
        <div className="flex w-full self-start justify-between mb-4">
          {isYWC ? (
            <YWCLogoSmall className="h-20 w-auto md:hidden" />
          ) : (
            <PublicLogo {...publicLogoProps} />
          )}
          <div className="flex items-center gap-2 ml-auto">
            <a
              href={t('faq.link')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 md:hidden"
              style={{ color: textColorDark }}
            >
              {t('faq.text_short')}
              <QuestionCircleOutlined />
            </a>
            <AntdConfigProvider theme={{ components: { Select: baseSelect } }}>
              <AntdSelect
                variant="borderless"
                defaultValue={i18n.language}
                onChange={(value) => changeLanguage(value, i18n)}
                aria-label="Language"
                options={languageOptions()}
              />
            </AntdConfigProvider>
          </div>
        </div>
        <div className="sm:hidden">
          <Title className="text-base-100 mb-4" style={{ color: textColorDark }}>
            {t('forgot_password_page.title')}
          </Title>
        </div>
        {email ? (
          <>
            <Text
              className="block mb-6"
              style={{ lineHeight: '18px' }}
              data-testid="reset-email-text"
            >
              <Trans i18nKey="forgot_password_page.email_recovery_success" values={{ email }}>
                If an account exists for this email you will shortly receive and email on{' '}
                <strong>{123}</strong> with password reset instructions.
                <br />
                <br />
                <Text className="block mb-8">
                  If the email doesn&apos;t show up soon, make sure there is an account created on
                  this email address and to check your spam folder or double check that you entered
                  the correct email address.
                </Text>
              </Trans>
            </Text>
            <Link to={`/${paths.sign_in}`}>
              <Button
                className="w-full"
                htmlType="submit"
                type="primary"
                data-testid="back-to-login-button"
              >
                {t('forgot_password_page.back_login')}
              </Button>
            </Link>
          </>
        ) : (
          <>
            <Text className="block mb-6">{t('forgot_password_page.email_instruction')}</Text>

            <AntdForm
              form={form}
              layout="vertical"
              onFinish={onSubmit}
              requiredMark={false}
              size={md ? 'middle' : 'large'}
            >
              <AntdForm.Item
                label={t('common.email_field_label')}
                name="email"
                rules={[
                  { required: true, message: t('validations.required') },
                  () => ({
                    validator(_, value) {
                      if (value && !isValidEmail(value))
                        return Promise.reject(new Error(t('validations.email')));
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input data-testid="email-input" className="h-[48px] sm:h-auto" inputMode="email" />
              </AntdForm.Item>

              <Text
                className="mb-4 block text-sm italic leading-relaxed"
                style={{ color: textColorDark }}
              >
                <Trans
                  t={t}
                  i18nKey="forgot_password_page.reset_password_privacy_notice"
                  components={[
                    <ExternalLink
                      href={t('privacy_policy.link')}
                      key="privacy"
                      className="text-primary-600 underline"
                    />,
                  ]}
                />
              </Text>

              <AntdForm.Item className="mb-2 mt-8">
                <Button
                  className="w-full"
                  htmlType="submit"
                  loading={passwordResetting}
                  type="primary"
                  data-testid="reset-password-button"
                >
                  {passwordResetting ? '' : t('forgot_password_page.button_submit')}
                </Button>
              </AntdForm.Item>
            </AntdForm>
            <div className="mt-4 sm:hidden">
              <Trans
                i18nKey="common.back_to_login"
                components={[
                  <Link
                    to={`/${paths.sign_in}`}
                    key="link"
                    className="underline ml-1"
                    style={{ color: textColorDark }}
                  />,
                ]}
              />
            </div>
            <AdditionalLogo {...additionalLogoProps} />
          </>
        )}
      </Card>
    </>
  );
};

export default ForgotPasswordPage;
